/**
 * Wire contract for the waiting-assignment queue (`GET /yard/view/waiting-assets`), shared by
 */

export enum WaitingAssetsSortField {
  CreateDateTime = 'createDateTime',
  LastChangedDateTime = 'lastChangedDateTime',
  AppointmentStart = 'appointmentStart'
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC'
}

export enum WaitingAssetsSortKey {
  LatestArrivals = 'latest-arrivals',
  EarliestArrivals = 'earliest-arrivals',
  LatestAppointments = 'latest-appointments',
  EarliestAppointments = 'earliest-appointments'
}

/** Lookback applied to the visit stream when the request names no window. */
export const WAITING_ASSETS_DEFAULT_WINDOW_DAYS = 7;

/**
 * Largest lookback a request may name. This bounds the input so the computed cutoff stays a valid
 * date; it is not a policy cap on the window. Past roughly 1e8 days the date is invalid, its ISO
 * form is null, and a falsy cutoff is dropped — which removes the window altogether.
 */
export const WAITING_ASSETS_MAX_ACCEPTED_WINDOW_DAYS = 365;

/** `arrivedWithinDays` value that removes the lookback, returning every waiting visit and container. */
export const WAITING_ASSETS_ALL_ARRIVALS_WINDOW_DAYS = 0;

export const WAITING_ASSETS_WINDOW_DAY_OPTIONS = [
  1,
  7,
  30,
  WAITING_ASSETS_ALL_ARRIVALS_WINDOW_DAYS
] as const;

export type WaitingAssetsWindowDays = (typeof WAITING_ASSETS_WINDOW_DAY_OPTIONS)[number];

export interface WaitingAssetsSortOption {
  sortField: WaitingAssetsSortField;
  sortOrder: SortOrder;
}

export const WAITING_ASSETS_SORT_OPTIONS: Readonly<
  Record<WaitingAssetsSortKey, WaitingAssetsSortOption>
> = {
  [WaitingAssetsSortKey.LatestArrivals]: {
    sortField: WaitingAssetsSortField.CreateDateTime,
    sortOrder: SortOrder.DESC
  },
  [WaitingAssetsSortKey.EarliestArrivals]: {
    sortField: WaitingAssetsSortField.CreateDateTime,
    sortOrder: SortOrder.ASC
  },
  [WaitingAssetsSortKey.LatestAppointments]: {
    sortField: WaitingAssetsSortField.AppointmentStart,
    sortOrder: SortOrder.DESC
  },
  [WaitingAssetsSortKey.EarliestAppointments]: {
    sortField: WaitingAssetsSortField.AppointmentStart,
    sortOrder: SortOrder.ASC
  }
};

export function isWaitingAssetsSortKey(value: unknown): value is WaitingAssetsSortKey {
  return Object.values(WaitingAssetsSortKey).includes(value as WaitingAssetsSortKey);
}

export function isWaitingAssetsWindowDays(value: unknown): value is WaitingAssetsWindowDays {
  return WAITING_ASSETS_WINDOW_DAY_OPTIONS.includes(value as WaitingAssetsWindowDays);
}
